import { Amount, ExchangeRate, Quantity } from "./amount.js";
import { Country } from "./countries.js";
import { Currency } from "./currencies.js";
import {
  ExpensesCategory,
  ExpensesType,
  IncomeType,
  IncomeCategory,
} from "./classifications.js";
import {
  VATCategory,
  InvoicevatExemption,
  WithheldTax,
  InvoiceStampDuty,
  InvoiceFee,
  InvoiceOtherTax,
  TaxTotals,
} from "./taxes.js";
import { ValuesOf } from "./typeutils.js";

export type Invoice = {
  /**
   * Αδυναμία σύνδεσης
   */
  transmissionFailure?: TransmissionFailure;
  /**
   * Εκδότης Παραστατικού
   */
  issuer: Entity;
  /**
   * Λήπτης Παραστατικού
   */
  counterpart?: Entity;
  /**
   * Επικεφαλίδα Παραστατικού
   */
  invoiceHeader: InvoiceHeader;
  /**
   * Τρόποι Πληρωμής
   */
  paymentMethods?: PaymentMethodDetail[];
  /**
   * Γραμμές Παραστατικού
   */
  invoiceDetails: InvoiceRow[];

  /**
   * 5.5 Σύνολα Φόρων
   *
   * Στο στοιχείο taxesTotals θα περιλαμβάνονται φόροι όλων των κατηγοριών,
   * εκτός του ΦΠΑ, οι οποίοι αφορούν όλο το παραστατικό σαν σύνολο.
   * Σε περίπτωση που ο χρήστης κάνει χρήση αυτού του στοιχείου,
   * δεν θα μπορεί να εισάγει φόρους εκτός του ΦΠΑ
   * σε κάθε γραμμή του παραστατικού ξεχωριστά
   *
   * Στην περίπτωση αποστολής γραμμών με
   * recType = 2 (γραμμή τέλους με ΦΠΑ) ή/και
   * recType = 3 (Γραμμή Λοιπών Φόρων με Φ.Π.Α.),
   * θα επιτρέπεται παράλληλα, εφόσον είναι επιθυμητό,
   * η αποστολή παρακρατούμενων/τελών/λοιπών φόρων/χαρτοσήμου/κρατήσεων
   * και σε επίπεδο παραστατικού
   * και όχι υποχρεωτικά ανά γραμμή σύνοψης παραστατικού.
   * Στις περιπτώσεις αυτών των γραμμών,
   * τα ποσά που αντιστοιχούν στα τέλη με ΦΠΑ (recType = 2)
   * ή τους λοιπούς φόρους (recType = 3) αντίστοιχα,
   * θα αποστέλλονται στο πεδίο της καθαρής αξίας της γραμμής (netValue),
   * ενώ τα αντίστοιχα πεδία ποσό τέλους (feesAmount) ή
   * ποσό λοιπών φόρων (otherTaxesAmount) δεν θα συμπληρώνονται.
   * Επίσης στις γραμμές αυτές δεν επιτρέπεται η αποστολή
   * άλλων ειδών φόρων/τελών/κρατήσεων/χαρτοσήμου
   * (π.χ. σε μια γραμμή με recType = 2 δεν επιτρέπονται στη γραμμή αυτή
   * η αποστολή λοιπών φόρων/κρατήσεων/παρακρατούμενων/χαρτοσήμου).
   */
  taxesTotals?: TaxTotals[];
  /**
   * Περίληψη Παραστατικού
   */
  invoiceSummary: InvoiceSummary;
};

/**
 * 5.1 Στοιχεία οντότητας
 */
export type Entity = {
  // Οποιοσδήποτε έγκυρος ΑΦΜ
  vatNumber: string;
  /**
   * Κωδικός Χώρας IS0 3166
   * Ο κωδικός της χώρας είναι δύο χαρακτήρες
   * και προέρχεται από την αντίστοιχη λίστα χωρών
   * όπως περιγράφεται στο ISO 3166.
   */
  country: Country;
  /**
   * Αρ. Εγκατάστασης
   * @minimum 0
   * @default 0
   *
   * Σε περίπτωση που η εγκατάσταση του εκδότη είναι η έδρα ή δεν υφίσταται,
   * το πεδίο branch πρέπει να έχει την τιμή 0
   */
  branch: number;
  /**
   * Επωνυμία
   */
  name?: string;
  /**
   * Διεύθυνση
   */

  address?: Address;

  /**
   * Αρ. Παροχής Ηλεκτρικού Ρεύματος
   * @maximum 100
   *
   * Μέγιστο επιτρεπτό μήκος 100.
   * Έγκυρο μόνο στην περίπτωση παραστατικών καυσίμων.
   *
   * Ο αριθμός Παροχής Ηλ. Ρεύματος, είναι επιτρεπτός μόνο στην περίπτωση
   * διαβίβασης παραστατικών καυσίμων (το πεδίο της επικεφαλίδας του παραστατικού
   * fuelInvoice έχει την τιμή true – αποδεκτό μόνο για διαβίβαση από παρόχους) και
   * είναι πληροφορία του λήπτη του παραστατικού.
   */
  supplyAccountNo?: string;
};

/**
 * 5.1.1 Διεύθυνση Οντότητας
 *
 * Η διεύθυνση του εκδότη (ή του λήπτη)
 */
export type Address = {
  /**
   * @minLength 1
   */
  street?: string;
  /**
   * @minimum 0
   * @integer true
   */
  number?: number;
  postalCode: string;
  city: string;
};

/**
 * 5.2 Τρόπος Πληρωμής
 */
export type PaymentMethodDetail = {
  /**
   * Τρόπος Πληρωμής
   */
  type: PaymentType;
  /**
   * Το πεδίο `amount` μπορεί να αντιστοιχεί σε ένα τμήμα της συνολικής αξίας
   * του παραστατικού
   */
  amount: Amount;
  /**
   * Το πεδίο Πληροφορίες μπορεί να περιέχει επιπλέον πληροφορίες σχετικά
   * με τον συγκεκριμένο τύπο (πχ Αρ. Λογαριασμού Τραπέζης)
   */
  paymentMethodInfo?: string;
  tipAmount?: any;
  transactionId?: any;
  ProvidersSignature?: {
    SigningAuthor: string,
    Signature: string,
  }
};

/**
 * 5.3 Επικεφαλίδα παραστατικού
 */
export type InvoiceHeader = {
  /**
   * Σειρά παραστατικού
   *
   * @maxLength 50
   * Σε περίπτωση μη έκδοσης σειράς παραστατικού,
   * το πεδίο series πρέπει να έχει την τιμή 0
   * @default '0'
   */
  series: string;

  /**
   * ΑΑ Παραστατικού
   *
   * TODO: find out if this needs to be sequential
   * and why it is typed as string
   *
   * @maxLength 50
   */
  aa: string;

  /**
   * Ημερομηνία Έκδοσης Παραστατικού
   * @format date
   */
  issueDate: string;

  /**
   *  Είδος Παραστατικού
   */
  invoiceType: InvoiceType;

  /**
   * Αναστολή Καταβολής ΦΠΑ
   * TODO: Is this for each AFM?
   */
  vatPaymentSuspension?: true;

  /**
   * Κωδικοί νομισμάτων
   *
   * Ο κωδικός νομισμάτων προέρχεται από την αντίστοιχη λίστα
   * σύμφωνα με το πρότυπο ISO4217.
   */
  currency?: Currency;

  /**
   * Ισοτιμία
   *
   * To πεδίο exchangeRate είναι η ισοτιμία του νομίσματος με το ευρώ.
   * Πρέπει να συμπληρώνεται μόνο όταν το νόμισμα δεν έχει τιμή EUR.
   */
  exchangeRate?: ExchangeRate;

  /**
   * Συσχετιζόμενα Παραστατικά
   *  Το στοιχείο correlatedInvoices είναι λίστα και περιέχει τα ΜΑΡΚ των
   * συσχετιζόμενων παραστατικών
   * "5.1"
   * "2.4"
   * "1.6"
   */
  correlatedInvoices?: string[];

  /**
   * Ένδειξη Αυτοτιμολόγησης
   * Το πεδίο selfPricing ορίζει αν πρόκειται για Τιμολόγιο Αυτοτιμολόγησης
   * @see https://texnologistiki.com/autotimologisi/
   * @see https://www.e-forologia.gr/cms/viewContents.aspx?id=45896
   * */
  selfPricing?: true;

  /**
   * Ημερομηνία Έναρξης Αποστολής
   *
   * @format date
   */
  dispatchDate?: string;

  /**
   * ´Ώρα Έναρξης Αποστολής
   *
   * @format time
   */
  dispatchTime?: string;

  /**
   * Αριθμός Μεταφορικού Μέσου
   */
  vehicleNumber?: string;

  /**
   * Σκοπός Διακίνησης
   */
  movePurpose?: DispatchPurpose;

  /**
   * Παραστατικό καυσίμων (ένδειξη)
   * Αποδεκτό μόνο στην περίπτωση αποστολής από παρόχους.
   * Το πεδίο fuelInvoice ορίζει αν πρόκειται για παραστατικό πώλησης υγρών
   * καυσίμων καυσίμων και επιτρέπεται η αποστολή μόνο για την περίπτωση των
   * παρόχων.
   */
  fuelInvoice?: true;
};

/**
 * Σειρά παραστατικού
 *
 * @maxLength 50
 * @pattern '^[A-Za-zo-9\.\-_]+$'
 * Σε περίπτωση μη έκδοσης σειράς παραστατικού, το πεδίο series πρέπει
 * να έχει την τιμή 0
 * @default '0'
 */
export type TaxSeriesName = string;

/**
 * 5.4 Στοιχεία παραστατικού
 */
export type InvoiceRow = {
  /**
   * ΑΑ γραμμής
   * @type integer
   * @minimum 1
   */
  lineNumber: number;
  /**
   *
   * Είδος Γραμμής
   * Ελάχιστη τιμή = 1
   * Μέγιστη τιμή = 6
   * Σημείωση: Στην παρούσα έκδοση οι τιμές 1, 4 και 5 δεν θα μπορούν
   * να χρησιμοποιηθούν – έχουν δεσμευτεί στο μοντέλο για μελλοντική χρήση
   *
   * Στην περίπτωση αποστολής γραμμών με recType = 2 (γραμμή τέλους με ΦΠΑ)
   * ή/και recType = 3 (Γραμμή Λοιπών Φόρων με Φ.Π.Α.),
   * θα επιτρέπεται παράλληλα, εφόσον είναι επιθυμητό,
   * η αποστολή παρακρατούμενων/τελών/λοιπών φόρων/χαρτοσήμου/κρατήσεων
   * και σε επίπεδο παραστατικού
   * και όχι υποχρεωτικά ανά γραμμή σύνοψης παραστατικού.
   *
   * Στις περιπτώσεις αυτών των γραμμών, τα ποσά που αντιστοιχούν
   * στα τέλη με ΦΠΑ (recType = 2) ή τους λοιπούς φόρους (recType = 3)
   * θα αποστέλλονται στο πεδίο της καθαρής αξίας της γραμμής (netValue),
   * ενώ τα αντίστοιχα πεδία ποσό τέλους (feesAmount) ή
   * ποσό λοιπών φόρων (otherTaxesAmount) δεν θα συμπληρώνονται.
   * Επίσης στις γραμμές αυτές δεν επιτρέπεται η αποστολή
   * άλλων ειδών φόρων/τελών/κρατήσεων/χαρτοσήμου (π.χ. σε μια γραμμή
   * με recType = 2 δεν επιτρέπονται στη γραμμή αυτή η αποστολή
   * λοιπών φόρων/κρατήσεων/παρακρατούμενων/χαρτοσήμου).
   */
  recType?: RowKind;
  /**
   * Κωδικός Καυσίμου
   *
   * (Λίστα τιμών – αναλυτικά οι τιμές στο αντίστοιχο παράρτημα)
   * Αποδεκτό μόνο στην περίπτωση αποστολής από παρόχους και για
   * την περίπτωση που το παραστατικό είναι παραστατικό καυσίμων
   * Οι πιθανές τιμές για το πεδίο fuelCode (κωδικός καυσίμου) περιγράφονται
   * αναλυτικά στον αντίστοιχο πίνακα του Παραρτήματος.
   * Eπιτρέπεται η αποστολή του μόνο για την περίπτωση των παρόχων και εφόσον
   * πρόκειται για παραστατικό καυσίμων (invoiceHeaderType.fuelInvoice = true)
   */
  fuelCode?: FuelCode;
  /**
   * Ποσότητα
   * @minimum 0
   */
  quantity?: Quantity;
  /** Είδος Ποσότητας */
  measurementUnit?: MeasurementUnit;

  /**
   * Επισήμανση
   *
   */
  // TODO: Διευκρινήσεις σχετικά με τις πωλήσεις για λογαριασμό τρίτων
  // και τις εκκαθαρίσεις/αμοιβές
  invoiceDetailType?: RowLabel;

  /**
   * Καθαρή αξία
   */
  netValue: Amount;

  /**
   * Κατηγορία ΦΠΑ
   *
   * Για περιπτώσεις λογιστικών εγγραφών όπου δεν εφαρμόζεται ΦΠΑ, το πεδίο
   * vatCategory θα έχει την τιμή 8
   */
  vatCategory: VATCategory;

  /** Ποσό ΦΠΑ */
  vatAmount: Amount;

  /**
   * Κατηγορία Αιτίας Εξαίρεσης ΦΠΑ
   * Το πεδίο vatExemptionCategory είναι απαραίτητο στην περίπτωση που το
   * vatCategory υποδηλώνει κατηγορία συντελεστή 0% ΦΠΑ
   */
  vatExemptionCategory?: InvoicevatExemption;

  /** ΠΟΛ 1177/2018 Αρ. 27 */
  dienergia?: Ship;

  /** Δικαίωμα Έκπτωσης */
  discountOption?: boolean;

  /** Ποσό Παρακράτησης Φόρου */
  withheldAmount?: Amount;
  /** Κατηγορία Συντελεστή Παρακράτησης */
  withheldPercentCategory?: WithheldTax;

  /** Ποσό Χαρτοσήμου */
  stampDutyAmount?: Amount;
  /** Κατηγορία Συντελεστή Χαρτοσήμου */
  stampDutyPercentCategory?: InvoiceStampDuty;

  /** Ποσό Τελών */
  feesAmount?: Amount;
  /** Κατηγορία Συντελεστή Τελών */
  feesPercentCategory?: InvoiceFee;

  /** Κατηγορία Συντελεστή Λοιπών Φόρων */
  otherTaxesPercentCategory?: InvoiceOtherTax;
  /** Άλλοι φόροι */
  otherTaxesAmount?: Amount;

  /** Ποσό Κρατήσεων */
  deductionsAmount?: Amount;

  /**
   * Σχόλια Γραμμής
   * Τα σχόλια γραμμής συμπληρώνονται από τον χρήστη και χρησιμοποιούνται για
   * πληροφοριακούς λόγους προς την υπηρεσία
   */
  lineComments?: string;

  /**
   * Χαρακτηρισμοί Εσόδων
   *
   * Οι χαρακτηρισμοί που αφορούν τον υποβάλλοντα
   * (εκδότης – εσόδων, λήπτης εξόδων),
   * υποβάλλονται μαζί με το παραστατικό με την αντίστοιχη
   * χρήση των πεδίων incomeClassification – expensesClassification
   */
  incomeClassification?: IncomeClassification[];
  /**
   * Χαρακτηρισμοί Εξόδων
   */
  expensesClassification?: ExpensesClassification[];
};

/**
 * 5.4.1 Δήλωση Διενέργειας (ΠΟΛ 1177/2018 Αρ. 27)
 *
 * Αφορά πλοία
 * ΠΟΛ 1177/2018 Αρ. 27
 */
export type Ship = {
  /**
   * Αριθμός Δήλωσης Διενέργειας Δραστηριότητας
   */
  applicationId: string;
  /**
   * Ημερομηνία Δήλωσης YYYY-MM-DD
   *
   * @format date
   */
  applicationDate: string;
  /**
   * ΔΟΥ Δήλωσης
   */
  doy?: string;
  /**
   * Στοιχεία Πλοίου
   */
  shipId: string;
};

/**
 * 5.6 Περίληψη παραστατικού
 */
export type InvoiceSummary = {
  /**
   * Σύνολο Καθαρής Αξίας
   */
  totalNetValue: Amount;
  /**
   * Σύνολο ΦΠΑ
   */
  totalVatAmount: Amount;
  /**
   * Όλα τα πεδία αθροισμάτων φόρων εκτός του totalVatAmount θα περιέχουν
   * είτε τα αθροίσματα των αντίστοιχων φόρων των γραμμών του παραστατικού,
   * είτε τα αθροίσματα των αντίστοιχων φόρων που περιέχονται
   * στο στοιχείο taxesTotals
   */

  /**
   * Σύνολο Παρακρατήσεων Φόρων
   */
  totalWithheldAmount: Amount;
  /**
   * Σύνολο Τελών
   */
  totalFeesAmount: Amount;
  /**
   * Σύνολο Χαρτοσήμου
   */
  totalStampDutyAmount: Amount;
  /**
   * Σύνολο Λοιπών Φόρων
   */
  totalOtherTaxesAmount: Amount;
  /**
   * Σύνολο Κρατήσεων
   */
  totalDeductionsAmount: Amount;
  /**
   * Συνολική Αξία
   */
  totalGrossValue: Amount;

  totalGrossValueNoTaxes?: Amount;
  /**
   * Τα στοιχεία incomeClassification και expensesClassification περιέχουν τα
   * αθροίσματα για κάθε συνδυασμό τιμών των πεδίων classificationType και
   * classificationCategory που εντοπίζονται στις γραμμές του παραστατικού
   */
  /**
   * Χαρακτηρισμοί Εσόδων
   */
  incomeClassification?: IncomeClassification[];
  /**
   * Χαρακτηρισμοί Εξόδων
   */
  expensesClassification?: ExpensesClassification[];
};

/**
 * 5.7 Χαρακτηρισμός Εσόδων
 *
 * Ο τύπος IncomeClassification (περιγράφεται παρακάτω) αποτελεί
 * την βασική δομή του Χαρακτηρισμού Εσόδων και εμπεριέχεται
 * είτε σε κάθε γραμμής του παραστατικού ξεχωριστά (χαρακτηρισμός γραμμής),
 * είτε στην περίληψη παραστατικού (άθροισμα χαρακτηρισμών ανά τύπο/κατηγορία),
 * είτε στο αντικείμενο InvoiceIncomeClassificationType
 * όταν οι χαρακτηρισμοί εσόδων υποβάλλονται ξεχωριστά
 * (βλ παράγραφος 4.3.2)
 */
export type IncomeClassification = {
  /**
   * Κωδικός Χαρακτηρισμού
   */
  classificationType?: IncomeType;
  /**
   * Κατηγορία Χαρακτηρισμού
   */
  classificationCategory: IncomeCategory;
  /**
   * Ποσό
   */
  amount: Amount;
  id?: number;
};

/**
 * 5.8 Χαρακτηρισμός Εξόδων
 *
 * Ο τύπος ExpensesClassification εμπεριέχεται:
 * - είτε σε κάθε γραμμή του παραστατικού ξεχωριστά (χαρ/σμός γραμμής),
 * - είτε στην περίληψη παραστατικού (άθροισμα χαρ/σμών ανά τύπο - κατηγορία),
 * - είτε στο αντικείμενο InvoiceExpensesClassificationType
 *   όταν οι χαρακτηρισμοί εσόδων υποβάλλονται ξεχωριστά (βλ παράγραφος 4.3.3)
 */
export type ExpensesClassification = {
  /**
   * Κωδικός Χαρακτηρισμού
   */
  classificationType?: ExpensesType;
  /**
   * Κατηγορία Χαρακτηρισμού
   */
  classificationCategory?: ExpensesCategory;
  /**
   * Ποσό
   */
  amount: Amount;
  id?: number;
};

/*
 * Αδυναμία Επικοινωνίας Παρόχου
 *
 * Αποδεκτό μόνο στην περίπτωση αποστολής από παρόχους.
 */
export const transmissionFailure = {
  /**
   * Αδυναμία σύνδεσης με τον πάροχο
   *
   * Αδυναμία επικοινωνίας εκδότη με τον πάροχο
   * κατά την έκδοση / διαβίβαση παραστατικού
   */
  Provider: 1,
  /**
   * Αδυναμία σύνδεσης με το MyDATA
   *
   * Αδυναμία επικοινωνίας του παρόχου με το myDATA
   * κατά την έκδοση / διαβίβαση παραστατικού
   */
  MyData: 2,
} as const;

export type TransmissionFailure = ValuesOf<typeof transmissionFailure>;

/**
 *  8.12 Τρόποι Πληρωμής
 * */
export const paymentType = {
  BankDepositDomestic: 1,
  BankDepositForeign: 2,
  Cash: 3,
  Check: 4,
  Credit: 5,
  WebBanking: 6, // added in v1.0.4
  POS: 7,
} as const;
export type PaymentType = ValuesOf<typeof paymentType>;

export const paymentTypeNames: Record<PaymentType, string> = {
  [paymentType.BankDepositDomestic]: "Κατάθεση σε τράπεζα εντός Ελλάδας",
  [paymentType.BankDepositForeign]: "Κατάθεση σε τράπεζα εξωτερικού",
  [paymentType.Cash]: "Μετρητά",
  [paymentType.Check]: "Επιταγή",
  [paymentType.Credit]: "Πίστωση",
  [paymentType.WebBanking]: "Πληρωμή μέσω Web Banking",
  [paymentType.POS]: "Πληρωμή με POS",
};

/**
 * 8.13 Είδος Ποσότητας
 */
export const measurementUnit = {
  Items: 1,
  Kilograms: 2,
  Liters: 3,
} as const;

export const measurementUnitNames = {
  [measurementUnit.Items]: "Τεμάχια",
  [measurementUnit.Kilograms]: "Κιλά",
  [measurementUnit.Liters]: "Λίτρα",
} as const;

export type MeasurementUnitEnum = "Items" | "Kilograms" | "Liters"

export const measurementUnitEnglishNames = {
  [measurementUnit.Items]: "items",
  [measurementUnit.Kilograms]: "kilograms",
  [measurementUnit.Liters]: "liters",
} as const;

export type MeasurementUnit = ValuesOf<typeof measurementUnit>;
export const isMeasurementUnit = (input?: unknown): input is MeasurementUnit =>
  typeof input === "number" &&
  Object.values(measurementUnit).includes(input as any);

/**
 * 8.14 Σκοπός Διακίνησης Κωδικός
 */
export const dispatchPurpose = {
  Sale: 1,
  SaleThirdParty: 2,
  Sampling: 3,
  Exposition: 4,
  Return: 5,
  Protection: 6,
  ProcessAssembly: 7,
  BetweenEntityFacilities: 8,
} as const;

export const dispatchPurposeNames = {
  [dispatchPurpose.Sale]: "Πώληση",
  [dispatchPurpose.SaleThirdParty]: "Πώληση για Λογαριασμό Τρίτων",
  [dispatchPurpose.Sampling]: "Δειγματισμός",
  [dispatchPurpose.Exposition]: "Έκθεση",
  [dispatchPurpose.Return]: "Επιστροφή",
  [dispatchPurpose.Protection]: "Φύλαξη",
  [dispatchPurpose.ProcessAssembly]: "Επεξεργασία / Συναρμολόγηση",
  [dispatchPurpose.BetweenEntityFacilities]: "Μεταξύ Εγκαταστάσεων Οντότητας",
} as const;

export type DispatchPurpose = ValuesOf<typeof dispatchPurpose>;
export const isDispatchPurpose = (input: unknown): input is DispatchPurpose =>
  typeof input === "number" &&
  Object.values(dispatchPurpose).includes(input as any);

/**
 * 8.15 Επισήμανση
 */
export const rowLabel = {
  ThirdPartySaleSettlement: 1,
  ThirdPartySaleReward: 2,
} as const;

export type RowLabel = ValuesOf<typeof rowLabel>;

/**
 * 8.16 Είδος Γραμμής
 */
export const rowKind = {
  // ΕιδικήΓραμμήΠαρακρατούμενωνΦόρων: 1, // Ανενεργός - για μελλοντική χρήση
  /**
   * Οι γραμμές με τέλη ανα τεμάχιο (πχ πλαστική σακούλα)
   * πρέπει να έχουν αυτό το είδος.
   * Στις γραμμές αυτές το netValue είναι το ποσό που αντιστοιχεί στο τέλος.
   * Το πεδίο feeAmount (και τα υπόλοια πεδία φόρων πλην ΦΠΑ) μένει κενό
   */
  FeeWithVAT: 2,
  /**
   * Οι γραμμές με λοιπούς φόρους ανα τεμάχιο (πχ φόρος διαμονής)
   * πρέπει να έχουν αυτό το είδος.
   * Στις γραμμές αυτές το netValue είναι το ποσό που αντιστοιχεί στο τέλος.
   * Το πεδίο feeAmount (και τα υπόλοια πεδία φόρων πλην ΦΠΑ) μένει κενό
   */
  OtherTaxWithVAT: 3,
  // ΕιδικήΓραμμήΧαρτοσήμου: 4, // Ανενεργός - για μελλοντική χρήση
  // ΕιδικήΓραμμήΚρατήσεων: 5, // Ανενεργός - για μελλοντική χρήση
  Giftcard: 6,
} as const;

export type RowKind = ValuesOf<typeof rowKind>;

/**
 *  8.17 Κωδικοί Καυσίμων
 * */
export const fuelCode = {
  Benzine95RON: 10,
  Benzine95RONPlus: 11,
  Benzine100RON: 12,
  BenzineLRP: 13,
  Diesel: 20,
  DieselPremium: 21,
  DieselHeatnn: 30,
  DieselHeatPremium: 31,
  DieselLinht: 32,
  LPG: 40,
  CNG: 50,
} as const;

export type FuelCode = ValuesOf<typeof fuelCode>;
export const isFuelCode = (code: unknown): code is FuelCode =>
  typeof code === "number" && Object.values(fuelCode).some((c) => c === code);

export const invoiceTypeNames = {
  "1.1": "Τιμολόγιο Πώλησης",
  "1.2": "Τιμολόγιο Πώλησης / Ενδοκοινοτικές Παραδόσεις",
  "1.3": "Τιμολόγιο Πώλησης / Παραδόσεις Τρίτων Χωρών",
  "1.4": "Τιμολόγιο Πώλησης για Λογαριασμό Τρίτων",
  "1.5": "Τιμολόγιο Πώλησης / Εκκαθάριση Πωλήσεων Τρίτων",
  "1.6": "Τιμολόγιο Πώλησης / Συμπληρωματικό Παραστατικό",

  "2.1": "Τιμολόγιο Παροχής Υπηρεσιών",
  "2.2": "Τιμολόγιο Παροχής / Ενδοκοινοτική Παροχή Υπηρεσιών",
  "3.1": "Τίτλος Κτήσης / Μη Υπόχρεος Εκδότης",
  "3.2": "Τίτλος Κτήσης / Άρνηση Έκδοσης από Υπόχρεο Εκδότη",
  "2.3": "Τιμολόγιο Παροχής / Παροχή Υπηρεσιών σε λήπτη Τρίτης Χώρας",
  "2.4": "Τιμολόγιο Παροχής Υπηρεσιών / Συμπληρωματικό Παραστατικό",

  "5.1": "Πιστωτικό Τιμολόγιο Μή Συσχετιζόμενο",
  "5.2": "Πιστωτικό Τιμολόγιο Συσχετιζόμενο",
  "6.1": "Στοιχείο Αυτοπαράδοσης",
  "6.2": "Στοιχείο Ιδιοχρησιμοποίησης",
  "7.1": "Συμβόλαιο Έσοδο",
  "8.1": "Ενοίκια - Έσοδο",
  "8.2": "Ειδικό Στοιχείο - Απόδειξης Είσπραξης Φόρου Διαμονής",
  "8.4": "Απόδειξη Είσπραξης POS",

  "11.1": "Απόδειξη Λιανικής Πώλησης",
  "11.2": "Απόδειξη Παροχής Υπηρεσιών",
  "11.3": "Απλοποιημένο Τιμολόγιο",
  "11.4": "Πιστωτικό Στοιχείο Λιανικής",
  "11.5": "Απόδειξη Λιανικής Πώλησης για Λογαριασμό Τρίτων",
} as const;

export type InvoiceType = keyof typeof invoiceTypeNames;

import { encode } from "iso-8859-7";
const importCrypto = async () => {
  let crypto = global.crypto;
  if (crypto !== undefined) {
    return crypto;
  }
  try {
    const { default: nodeCrypto } = await import("node:crypto");
    return nodeCrypto.webcrypto;
  } catch (error) {
    throw new Error("crypto is not available");
  }
};

export const invoiceUID = async (invoice: Invoice) => {
  const webcrypto = await importCrypto();
  const payloadUTF8 = [
    invoice.issuer.vatNumber,
    String(invoice.issuer.branch),
    invoice.invoiceHeader.issueDate,
    invoice.invoiceHeader.invoiceType,
    invoice.invoiceHeader.series,
    invoice.invoiceHeader.aa,
  ].join("");
  const payloadISO88597 = encode(payloadUTF8);
  const digestSHA1 = await webcrypto.subtle.digest("SHA-1", payloadISO88597);
  const uid = toHexString(new Uint8Array(digestSHA1));
  return uid;
};

const toHexString = (buf: Uint8Array) =>
  Array.from(buf)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase();


const delay = (ms: number) => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

const getPayload = (invoiceId: string, mark: string, dateTime: string, payableAmount: string, netValue: string, totalVatValue: string, grossValue: string, terminalId: string) => {
  // UID;MARK;DATETIME;PAYABLE_AMOUNT;NET_VALUE;VAT;TOTAL_INV_AMOUNT;TERMINAL_ID

  return `${invoiceId};${mark};${dateTime};${payableAmount};${netValue};${totalVatValue};${grossValue};${terminalId}`;
};


export const submitAmountForSignature = "Υποβολή ποσού για υπογραφή";
const awaitingSignature = "Αναμονή υπογραφής";
const signatureReceived = "Υπογραφή ποσού";
const submitAmountToPOS = "Υποβολή ποσού σε POS";
const waitingResponseFromPOS = "Αναμονή απάντησης από POS";
const responseReceivedFromPOS = "Απάντηση από POS";

export const posPayment = async (invoiceId: string, mark: string, dateTime: string, payableAmount: string, netValue: string, totalVatValue: string, grossValue: string, terminalId: string, statusFunc: any, rpc: any, invoiceType: string | null) => {
    // 1. prepare payload
    const payload = getPayload(invoiceId, mark, dateTime, payableAmount, netValue, totalVatValue, grossValue, terminalId);
    statusFunc(awaitingSignature);
    await delay(500);

    // 2. send payload for signature
    const signObject: {signature: string, publicKey: string} = await rpc.mutation("signPayload", payload);
    statusFunc(signatureReceived);
    await delay(500);

    const paymentObjData: any = {
      invoiceId: invoiceId,
      paymentType: "7",  // POS payment 7
      posPayload: payload,
      paymentAmt: parseFloat(payableAmount),
      providerSignature: signObject.signature,
      status: "pending",
    };

    if (invoiceType) {
      paymentObjData["invoiceType"] = invoiceType
    }

    // 3. create payment object in db
    const paymentObj = await rpc.mutation("createPayment", paymentObjData);

    statusFunc(submitAmountToPOS)
    await delay(500);

    statusFunc(waitingResponseFromPOS)
    await delay(500);

    // 3. send the payload, signature and pub key to POS and get transaction id
    const posResponse = await rpc.mutation("postToPos", {
      payload,
      signature: signObject.signature,
      publicKey: signObject.publicKey
    });

    statusFunc(responseReceivedFromPOS)
    console.log("--posResponse--", posResponse)

    return paymentObj.payment.id;
}